import { useResource } from '../useResource';

type Override<T1, T2> = Omit<T1, keyof T2> & T2;

export type LayerImage = {
  id: number;
  is_group: false;
  name: string;
  s3_key_trimmed: string;
  s3_key_fullsize: string;
  nested_layers: [];
  size: [number, number];
  bbox: [number, number, number, number];
  atlas_bbox: [number, number, number, number] | null;
  opacity: number;
  visible: boolean;
};

export type LayerGroup = Override<
  LayerImage,
  {
    is_group: true;
    s3_key_trimmed: null;
    s3_key_fullsize: null;
    nested_layers: Layer[];
    atlas_bbox: null;
  }
>;

export type Layer = LayerImage | LayerGroup;

export type LayersData = {
  layers: Layer[];
  original_image: {
    size: [number, number];
  };
  atlas: {
    s3_key: string;
    size: number;
    resolution: [number, number];
  };
};

export type DownloadOnClickParams = {
  layerName: Layer['name'];
  fileName: string;
  isGroup: boolean;
  isFullSize: boolean;
  url: string;
};

export type LayerToolOptions = {
  loadLayersUrl: string;
  viewUrlFn: (fileName: string) => string;
  downloadAvailable: boolean;
  downloadUrlFn: (fileName: string) => string;
  downloadGroupUrlFn: (layerId: Layer['id'], isFullSize: boolean) => string;
  downloadOnClick?: (params: DownloadOnClickParams) => void;
  layersButtonOnClick?: () => void;
};

export function useLayers(loadLayersUrl?: string) {
  const { data, error } = useResource<LayersData>(loadLayersUrl);
  return { layersData: data, error };
}

export type Expanded = Partial<Record<Layer['id'], boolean>>;

export type Visible = Partial<Record<Layer['id'], boolean>>;

export enum VisibilityStatusEnum {
  visible,
  invisible,
  parentInvisible,
}
export type VisibilityStatus = Partial<Record<Layer['id'], VisibilityStatusEnum>>;

export type MenuItem = { layer: Layer; indent: number; isLast: boolean };

export function createMenuItems(layers: Layer[], expanded: Expanded) {
  function process(layers: Layer[], items: MenuItem[], indent: number) {
    layers.forEach((layer, index) => {
      items.push({ layer, indent, isLast: index === layers.length - 1 });
      if (layer.is_group && expanded[layer.id]) {
        process(layer.nested_layers, items, indent + 1);
      }
    });
    return items;
  }
  return process(layers, [], 0);
}

export function initVisible(layers: Layer[]) {
  const visible: Visible = {};
  treeForEach(layers, (layer) => {
    if (layer.visible) {
      visible[layer.id] = true;
    }
  });
  return visible;
}

export function switchAllVisible(layers: Layer[], value: boolean) {
  const visible: Visible = {};
  treeForEach(layers, (layer) => {
    visible[layer.id] = value;
  });
  return visible;
}

export function checkAllVisible(layers: Layer[], visible: Visible) {
  return !treeFind(layers, (layer) => !visible[layer.id]);
}

export function getVisibilityStatus(layers: Layer[], visible: Visible) {
  function process(layers: Layer[], visibilityStatus: VisibilityStatus, parentInvisible: boolean) {
    layers.forEach((layer) => {
      if (!visible[layer.id]) {
        visibilityStatus[layer.id] = VisibilityStatusEnum.invisible;
      } else if (parentInvisible) {
        visibilityStatus[layer.id] = VisibilityStatusEnum.parentInvisible;
      } else {
        visibilityStatus[layer.id] = VisibilityStatusEnum.visible;
      }

      process(
        layer.nested_layers,
        visibilityStatus,
        visibilityStatus[layer.id] !== VisibilityStatusEnum.visible,
      );
    });
    return visibilityStatus;
  }
  return process(layers, {}, false);
}

export const layerElementId = (layerId: Layer['id']) => `gen-layers__layer_${layerId}`;
export const atlasId = 'gen-layers__atlas';

function treeForEach(layers: Layer[], iterator: (layer: Layer) => void) {
  layers.forEach((layer) => {
    iterator(layer);
    treeForEach(layer.nested_layers, iterator);
  });
}

function treeFind(layers: Layer[], predicate: (layer: Layer) => boolean): Layer | undefined {
  let result;
  for (let i = 0; result === undefined && i < layers.length; i++) {
    if (predicate(layers[i])) {
      result = layers[i];
    } else {
      result = treeFind(layers[i].nested_layers, predicate);
    }
  }
  return result;
}
